import { Pause, Play, Shuffle } from 'lucide-react';
import type { Track } from '@/types';
import { isPlayable } from '@/lib/api';
import { usePlayer } from '@/hooks/usePlayer';

/** Circular play/pause for the release detail heading. */
export function ReleasePlayButton({
  tracks,
  className = 'detail-title-play',
  showLabel = false,
}: {
  tracks: Track[];
  className?: string;
  /** When true, show “Play” / “Pause” text beside the icon (mobile transport). */
  showLabel?: boolean;
}) {
  const {
    playAlbum,
    playing,
    sessionActive,
    radioMode,
    tracks: playerTracks,
  } = usePlayer();
  const hasPlayable = tracks.some(isPlayable);
  const isActiveQueue =
    tracks.length > 0 &&
    tracks.length === playerTracks.length &&
    tracks.every((t, i) => t.id === playerTracks[i]?.id);
  const albumPlaying = Boolean(
    sessionActive && playing && !radioMode && isActiveQueue,
  );

  return (
    <button
      type="button"
      className={className}
      disabled={!hasPlayable}
      aria-label={albumPlaying ? 'Pause album' : 'Play album'}
      onClick={playAlbum}
    >
      {albumPlaying ? (
        <Pause size={16} fill="currentColor" />
      ) : (
        <Play size={16} fill="currentColor" />
      )}
      {showLabel ? <span>{albumPlaying ? 'Pause' : 'Play'}</span> : null}
    </button>
  );
}

/** Circular shuffle-play for the release detail transport row. */
export function ReleaseShuffleButton({
  tracks,
  className = 'detail-title-shuffle',
}: {
  tracks: Track[];
  className?: string;
}) {
  const { shufflePlay, shuffleMode, sessionActive, radioMode, tracks: playerTracks } =
    usePlayer();
  const hasPlayable = tracks.some(isPlayable);
  const isActiveQueue =
    tracks.length > 0 &&
    tracks.length === playerTracks.length &&
    tracks.every((t, i) => t.id === playerTracks[i]?.id);
  const shuffleActive = Boolean(
    sessionActive && shuffleMode && !radioMode && isActiveQueue,
  );

  return (
    <button
      type="button"
      className={className}
      disabled={!hasPlayable}
      aria-label="Shuffle play album"
      aria-pressed={shuffleActive}
      onClick={shufflePlay}
    >
      <Shuffle size={16} />
    </button>
  );
}
